// components/OgrenciPastLessonsModal.js
import React, { useState, useMemo, useEffect, useRef } from 'react';

const OgrenciPastLessonsModal = ({
  isOpen,
  onClose,
  studentInfo,
  lessons,
  loading,
  teachers,
  lessonGroups
}) => {
  // Filtreleme state'leri
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    teacherId: 'all',
    teacherSearch: '', // Öğretmen arama metni
    lessonGroupId: 'all',
    smsStatus: 'all'
  });

  // Filtreleri açık/kapalı state'i - VARSAYILAN OLARAK AÇIK
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  
  // Öğretmen dropdown açık/kapalı state'i
  const [isTeacherDropdownOpen, setIsTeacherDropdownOpen] = useState(false);
  
  // Dropdown ref'i (dışarı tıklamayı algılamak için)
  const teacherDropdownRef = useRef(null);

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id == teacherId);
    return teacher ? teacher.adi : '-';
  };

  const getLessonGroupName = (lessonId) => {
    const lesson = lessonGroups.find(l => l.id == lessonId);
    return lesson ? lesson.ders_adi : '-';
  };

  // Benzersiz öğretmenleri al (dropdown için)
  const uniqueTeachers = useMemo(() => {
    if (!lessons || lessons.length === 0) return [];
    const teacherIds = [...new Set(lessons.map(l => l.ogretmen_id))];
    return teacherIds.map(id => ({
      id,
      name: getTeacherName(id)
    })).filter(t => t.name !== '-');
  }, [lessons, teachers]);

  // Arama metnine göre filtrelenmiş öğretmenler
  const filteredTeachers = useMemo(() => {
    if (!uniqueTeachers || uniqueTeachers.length === 0) return [];
    if (!filters.teacherSearch) return uniqueTeachers;
    
    const searchTerm = filters.teacherSearch.toLowerCase();
    return uniqueTeachers.filter(teacher => 
      teacher.name.toLowerCase().includes(searchTerm)
    );
  }, [uniqueTeachers, filters.teacherSearch]);

  // Benzersiz ders gruplarını al (dropdown için)
  const uniqueLessonGroups = useMemo(() => {
    if (!lessons || lessons.length === 0) return [];
    const lessonIds = [...new Set(lessons.map(l => l.ders_id))];
    return lessonIds.map(id => ({
      id,
      name: getLessonGroupName(id)
    })).filter(l => l.name !== '-');
  }, [lessons, lessonGroups]);

  // Filtrelenmiş dersler
  const filteredLessons = useMemo(() => {
    if (!lessons || lessons.length === 0) return [];
    
    return lessons.filter(lesson => {
      // Tarih filtresi
      if (filters.startDate) {
        const lessonDate = new Date(lesson.baslangic_tarihi);
        const startDate = new Date(filters.startDate);
        if (lessonDate < startDate) return false;
      }
      if (filters.endDate) {
        const lessonDate = new Date(lesson.baslangic_tarihi);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        if (lessonDate > endDate) return false;
      }

      // Öğretmen filtresi
      if (filters.teacherId !== 'all' && lesson.ogretmen_id != filters.teacherId) {
        return false;
      }

      // Ders grubu filtresi
      if (filters.lessonGroupId !== 'all' && lesson.ders_id != filters.lessonGroupId) {
        return false;
      }

      // SMS durumu filtresi
      if (filters.smsStatus !== 'all') {
        const smsStatus = filters.smsStatus === '1' ? 1 : 0;
        if (lesson.sms_status !== smsStatus) return false;
      }

      return true;
    });
  }, [lessons, filters]);

  // Toplam hesaplamalar (orijinal veri)
  const originalTotals = useMemo(() => {
    if (!lessons || lessons.length === 0) {
      return {
        count: 0,
        dersUcret: 0,
        sirketPayi: 0,
        danisman1: 0,
        danisman2: 0,
        danisman3: 0,
        genel: 0
      };
    }
    
    return {
      count: lessons.length,
      dersUcret: lessons.reduce((sum, lesson) => sum + (parseFloat(lesson.ders_ucret) || 0), 0),
      sirketPayi: lessons.reduce((sum, lesson) => sum + (parseFloat(lesson.sirket_payi) || 0), 0),
      danisman1: lessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman1_payi || lesson.danisman1) || 0), 0),
      danisman2: lessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman2_payi || lesson.danisman2) || 0), 0),
      danisman3: lessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman3_payi || lesson.danisman3) || 0), 0),
      genel: lessons.reduce((sum, lesson) => 
        sum + (
          parseFloat(lesson.ders_ucret || 0) + 
          parseFloat(lesson.sirket_payi || 0) + 
          parseFloat(lesson.danisman1_payi || lesson.danisman1 || 0) + 
          parseFloat(lesson.danisman2_payi || lesson.danisman2 || 0) + 
          parseFloat(lesson.danisman3_payi || lesson.danisman3 || 0)
        ), 0)
    };
  }, [lessons]);

  // Filtrelenmiş toplam hesaplamalar
  const filteredTotals = useMemo(() => {
    if (!filteredLessons || filteredLessons.length === 0) {
      return {
        count: 0,
        dersUcret: 0,
        sirketPayi: 0,
        danisman1: 0,
        danisman2: 0,
        danisman3: 0,
        genel: 0
      };
    }
    
    return {
      count: filteredLessons.length,
      dersUcret: filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.ders_ucret) || 0), 0),
      sirketPayi: filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.sirket_payi) || 0), 0),
      danisman1: filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman1_payi || lesson.danisman1) || 0), 0),
      danisman2: filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman2_payi || lesson.danisman2) || 0), 0),
      danisman3: filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.danisman3_payi || lesson.danisman3) || 0), 0),
      genel: filteredLessons.reduce((sum, lesson) => 
        sum + (
          parseFloat(lesson.ders_ucret || 0) + 
          parseFloat(lesson.sirket_payi || 0) + 
          parseFloat(lesson.danisman1_payi || lesson.danisman1 || 0) + 
          parseFloat(lesson.danisman2_payi || lesson.danisman2 || 0) + 
          parseFloat(lesson.danisman3_payi || lesson.danisman3 || 0)
        ), 0)
    };
  }, [filteredLessons]);

  // Aktif filtre sayısını hesapla
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.teacherId !== 'all') count++;
    if (filters.lessonGroupId !== 'all') count++;
    if (filters.smsStatus !== 'all') count++;
    return count;
  }, [filters]);

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      teacherId: 'all',
      teacherSearch: '',
      lessonGroupId: 'all',
      smsStatus: 'all'
    });
    setIsTeacherDropdownOpen(false);
  };

  // Öğretmen seçimi
  const selectTeacher = (teacherId, teacherName) => {
    setFilters({
      ...filters,
      teacherId: teacherId,
      teacherSearch: teacherName
    });
    setIsTeacherDropdownOpen(false);
  };

  // Dışarı tıklamayı algıla
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teacherDropdownRef.current && !teacherDropdownRef.current.contains(event.target)) {
        setIsTeacherDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Modal kapalıysa render etme
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 transition-all duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 scale-100">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-800">
              Geçmiş Dersler
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {studentInfo?.name} - 
              {activeFilterCount > 0 ? (
                <span>
                  <span className="font-semibold text-indigo-600">{filteredTotals.count}</span> / {originalTotals.count} ders gösteriliyor
                  <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                    {activeFilterCount} filtre aktif
                  </span>
                </span>
              ) : (
                <span>Toplam {originalTotals.count} ders</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                isFilterOpen || activeFilterCount > 0
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtrele
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button 
              onClick={onClose} 
              className="p-2 rounded-full hover:bg-slate-100 transition-colors duration-200 group"
            >
              <svg
                className="h-6 w-6 text-slate-400 group-hover:text-slate-600 transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filtreleme Paneli */}
        {isFilterOpen && (
          <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-indigo-300 shadow-md">
            {/* Filtreleme Başlığı */}
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-bold text-indigo-800 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                FİLTRELEME PANELİ - Tarih, Öğretmen, Ders ve SMS Durumuna Göre Filtrele
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {/* Başlangıç Tarihi */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>

              {/* Bitiş Tarihi */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>

              {/* Öğretmen Filtresi - ARAÇLI VE SEÇMELİ */}
              <div className="relative" ref={teacherDropdownRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Öğretmen (Yazın veya Seçin)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={filters.teacherSearch}
                    onChange={(e) => {
                      setFilters({ ...filters, teacherSearch: e.target.value, teacherId: 'all' });
                      setIsTeacherDropdownOpen(true);
                    }}
                    onFocus={() => setIsTeacherDropdownOpen(true)}
                    placeholder="Öğretmen ara veya seç..."
                    className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setIsTeacherDropdownOpen(!isTeacherDropdownOpen)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isTeacherDropdownOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                    </svg>
                  </button>
                </div>
                
                {/* Dropdown Listesi */}
                {isTeacherDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => selectTeacher('all', '')}
                      className="w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm font-semibold text-indigo-600 border-b border-slate-200"
                    >
                      Tüm Öğretmenler
                    </button>
                    {filteredTeachers.length > 0 ? (
                      filteredTeachers.map(teacher => (
                        <button
                          key={teacher.id}
                          type="button"
                          onClick={() => selectTeacher(teacher.id, teacher.name)}
                          className={`w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm transition-colors ${
                            filters.teacherId === teacher.id ? 'bg-indigo-100 text-indigo-700 font-semibold' : ''
                          }`}
                        >
                          {teacher.name}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-slate-500 italic">
                        Öğretmen bulunamadı
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Ders Grubu Filtresi */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Ders Grubu</label>
                <select
                  value={filters.lessonGroupId}
                  onChange={(e) => setFilters({ ...filters, lessonGroupId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="all">Tüm Dersler</option>
                  {uniqueLessonGroups.map(lesson => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* SMS Durumu Filtresi */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">SMS Durumu</label>
                <select
                  value={filters.smsStatus}
                  onChange={(e) => setFilters({ ...filters, smsStatus: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="all">Tümü</option>
                  <option value="1">Gönderildi</option>
                  <option value="0">Gönderilmedi</option>
                </select>
              </div>
            </div>

            {/* Filtre İşlemleri */}
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-indigo-200">
              <button
                onClick={clearFilters}
                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 font-semibold flex items-center shadow-md"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                TÜM FİLTRELERİ TEMİZLE
              </button>
              <div className="text-lg font-bold text-indigo-800 bg-white px-4 py-2 rounded-lg border-2 border-indigo-300">
                <span className="text-2xl font-bold text-indigo-600">{filteredTotals.count}</span> DERS BULUNDU
              </div>
            </div>
          </div>
        )}

        {/* İstatistik Kartları - Filtre aktifse göster */}
        {activeFilterCount > 0 && (
          <div className="px-4 sm:px-6 py-3 bg-amber-50 border-b border-amber-200">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 border border-amber-200">
                <div className="text-xs text-slate-600 mb-1">Filtrelenmiş / Toplam Ders</div>
                <div className="text-lg font-bold text-slate-800">
                  <span className="text-indigo-600">{filteredTotals.count}</span> / {originalTotals.count}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-200">
                <div className="text-xs text-slate-600 mb-1">Filtrelenmiş / Toplam Ücret</div>
                <div className="text-lg font-bold text-emerald-600">
                  ₺{filteredTotals.dersUcret.toFixed(2)} / ₺{originalTotals.dersUcret.toFixed(2)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-200">
                <div className="text-xs text-slate-600 mb-1">Filtrelenmiş / Genel Toplam</div>
                <div className="text-lg font-bold text-green-600">
                  ₺{filteredTotals.genel.toFixed(2)} / ₺{originalTotals.genel.toFixed(2)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-200">
                <div className="text-xs text-slate-600 mb-1">Filtreleme Oranı</div>
                <div className="text-lg font-bold text-purple-600">
                  %{originalTotals.count > 0 ? ((filteredTotals.count / originalTotals.count) * 100).toFixed(1) : 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Dersler yükleniyor...</p>
              </div>
            </div>
          ) : filteredLessons.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-slate-500">
                  {activeFilterCount > 0 
                    ? 'Filtreleme kriterlerine uygun ders bulunamadı.' 
                    : 'Bu öğrenciye ait ders kaydı bulunamadı.'}
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                  >
                    Filtreleri Temizle
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-max table-auto">
                <thead className="bg-slate-100">
                  <tr className="text-slate-600 uppercase text-xs font-semibold tracking-wider">
                    <th className="px-4 py-3 text-left border-b border-slate-200">Öğretmen</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Ders Grubu</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Konu</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Süre</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Ekleme Tarihi</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Başlangıç Tarihi</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Ücret</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Şirket</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Dan. 1</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Dan. 2</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Dan. 3</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">Toplam</th>
                    <th className="px-4 py-3 text-left border-b border-slate-200">SMS</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredLessons.map((lesson) => (
                    <tr key={lesson.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <div className="font-medium text-slate-700">{getTeacherName(lesson.ogretmen_id)}</div>
                          <div className="text-xs text-slate-500">{lesson.ogretmen_telefon || '-'}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {getLessonGroupName(lesson.ders_id) || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{lesson.ders_konusu || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{lesson.ders_sure ? `${lesson.ders_sure} dk` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {lesson.created_at
                          ? new Date(lesson.created_at).toLocaleDateString('tr-TR')
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {lesson.baslangic_tarihi
                          ? new Date(lesson.baslangic_tarihi).toLocaleDateString('tr-TR')
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-emerald-600">₺{parseFloat(lesson.ders_ucret || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-xs">
                          <div className="text-gray-500">{lesson.sirket_adi || '-'}</div>
                          <div className="font-medium text-orange-600">₺{parseFloat(lesson.sirket_payi || 0).toFixed(2)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-xs">
                          <div className="text-gray-500">{lesson.danisman1_adi || '-'}</div>
                          <div className="font-medium text-blue-600">₺{parseFloat(lesson.danisman1_payi || lesson.danisman1 || 0).toFixed(2)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-xs">
                          <div className="text-gray-500">{lesson.danisman2_adi || '-'}</div>
                          <div className="font-medium text-blue-600">₺{parseFloat(lesson.danisman2_payi || lesson.danisman2 || 0).toFixed(2)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-xs">
                          <div className="text-gray-500">{lesson.danisman3_adi || '-'}</div>
                          <div className="font-medium text-blue-600">₺{parseFloat(lesson.danisman3_payi || lesson.danisman3 || 0).toFixed(2)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600">
                        ₺{(
                          parseFloat(lesson.ders_ucret || 0) + 
                          parseFloat(lesson.sirket_payi || 0) + 
                          parseFloat(lesson.danisman1_payi || lesson.danisman1 || 0) + 
                          parseFloat(lesson.danisman2_payi || lesson.danisman2 || 0) + 
                          parseFloat(lesson.danisman3_payi || lesson.danisman3 || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          lesson.sms_status === 1 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {lesson.sms_status === 1 ? 'Gönderildi' : 'Gönderilmedi'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan="6" className="px-4 py-3 text-sm font-semibold text-slate-700 text-right">
                      {activeFilterCount > 0 ? 'Filtrelenmiş Toplam:' : 'Toplam:'}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-700">
                      ₺{filteredTotals.dersUcret.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-orange-700">
                      ₺{filteredTotals.sirketPayi.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-700">
                      ₺{filteredTotals.danisman1.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-700">
                      ₺{filteredTotals.danisman2.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-700">
                      ₺{filteredTotals.danisman3.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-green-700">
                      ₺{filteredTotals.genel.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col sm:flex-row gap-4">
              {activeFilterCount > 0 ? (
                <>
                  {/* Filtrelenmiş Toplamlar */}
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="text-xs text-indigo-600 font-semibold mb-1">FİLTRELENMİŞ</div>
                    <div className="text-sm text-slate-700">
                      <div>Eğitim Ücreti: <span className="font-bold text-indigo-600">₺{filteredTotals.dersUcret.toFixed(2)}</span></div>
                      <div>Genel Toplam: <span className="font-bold text-green-600">₺{filteredTotals.genel.toFixed(2)}</span></div>
                      <div>Ders Sayısı: <span className="font-bold">{filteredTotals.count}</span></div>
                    </div>
                  </div>
                  
                  {/* Orijinal Toplamlar */}
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-600 font-semibold mb-1">TÜM VERİ</div>
                    <div className="text-sm text-slate-700">
                      <div>Eğitim Ücreti: <span className="font-bold text-indigo-600">₺{originalTotals.dersUcret.toFixed(2)}</span></div>
                      <div>Genel Toplam: <span className="font-bold text-green-600">₺{originalTotals.genel.toFixed(2)}</span></div>
                      <div>Ders Sayısı: <span className="font-bold">{originalTotals.count}</span></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">Toplam Eğitim Ücreti:</span>{' '}
                    <span className="font-bold text-indigo-600">
                      ₺{originalTotals.dersUcret.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">Genel Toplam:</span>{' '}
                    <span className="font-bold text-green-600">
                      ₺{originalTotals.genel.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OgrenciPastLessonsModal;